import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Grid,
    Breadcrumbs,
    Typography,
    Button,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Table,
    Paper,
    TableBody,
    IconButton,
    Switch, Stack,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import LinkNavegacao from '../../../common/Link';
import PageTitle from '../../../common/PageTitle';
import { EditIcon, SewingTapeMeasureIcon, TrashIcon } from '../../../common/Icons';
import {
    listaUnidadesDeMedidas,
    atualizaUnidadesDeMedida,
    deletaUnidadesDeMedida,
    listaTamanhosEVariacoes
} from './configs/functions';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { UnitsOfMeasureForm } from './UnitsOfMeasureForm';
import {useAppContext} from "../../../../contexts/AppContext";
import {useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {listaEmpresas} from "../../management/companies/configs/functions";
import {FiltersButton} from "../../../common/FiltersButton";
import {ItemsNoCodeTableFilter} from "../../management/organization/components/ItemsNoCodeTableFilter";
import Alert from "@mui/material/Alert";
import {SkeletonTableRow} from "../../../common/SkeletonTableRow";

const UnitOfMeasureRow = ({ data, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData, hasContract }) => {
    const [active, setActive] = useState(data.status);
    const [openDialog, setOpenDialog] = useState(false);

    async function handleToggleStatus() {
        if (!hasPermission(["admin", "admin_unidadeMedidas", "update_unidadeMedidas"])) {
            handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
            return false;
        }

        try {
            const res = await atualizaUnidadesDeMedida(data.id, { status: !active })
            if (res) {
                const message = active ? 'Unidade de Medida inativada com sucesso' : 'Unidade de Medida ativada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success')
                setActive(!active)
                handleUpdateStatus(data.id, !data.status)
            }
        } catch (error) {
            handleExibirAlerta('Erro', 'Erro ao alterar status', 'error')
        }
    }

    const deleteCostCenter = () => {
        const id = data.id;
        deletaUnidadesDeMedida(id)
            .then((response) => {
                const message = response.data.message ?? 'Unidade de Medida deletada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success');
                handleUpdateStatus(id, false);
                setActive(false);
                updateData();
            }).catch((error) => {
                const message = error.response.data.message ?? 'Erro ao executar operação'
                handleExibirAlerta('Erro', message, 'error');
            })

        setOpenDialog(false)
    }

    return (
        <>
            <TableRow
                key={data.id}
                aria-disabled={!hasContract}
                sx={{
                    opacity: !hasContract ? 0.5 : 1,
                    pointerEvents: !hasContract ? "none" : "auto",
                    backgroundColor: !hasContract ? "action.disabledBackground" : "inherit"
                }}
            >
                <TableCell>{data.nome}</TableCell>
                <TableCell>{data.descricao}</TableCell>
                <TableCell>{data.observacao}</TableCell>

                <TableCell>
                    <Switch
                        checked={active}
                        onChange={handleToggleStatus}
                        disabled={!hasPermission(["admin", "admin_unidadeMedidas", "update_unidadeMedidas"])}
                    />
                </TableCell>

                <TableCell align='right' sx={{ minWidth: 110 }}>
                    {hasPermission(["admin", "admin_unidadeMedidas", "update_unidadeMedidas"]) &&
                        <IconButton
                            aria-label="Editar"
                            onClick={() => handleOpenEditForm(data)}
                        >
                            <EditIcon />
                        </IconButton>
                    }

                    {hasPermission(["admin", "admin_unidadeMedidas", "delete_unidadeMedidas"]) &&
                        <IconButton
                            aria-label="Excluir"
                            onClick={() => setOpenDialog(true)}
                        >
                            <TrashIcon />
                        </IconButton>
                    }
                </TableCell>
            </TableRow>

            <ConfirmDialog
                description={'Tem certeza que deseja excluir esta Unidade de Medida?'}
                title={`Excluir Unidade de Medida ${data.nome}`}
                goAction={deleteCostCenter}
                handleClose={setOpenDialog}
                state={openDialog}
            />
        </>
    )
}

function ListOfUnitsOfMeasure() {
    const { exibirAlerta } = useCommonItems();
    const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

    // estados para controle de paginacao
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [numTotalItems, setNumTotalItems] = useState(0);

    const [unidadesDeMedidas, setUnidadesDeMedidas] = useState(null);
    const [hasContract, setHasContract] = useState(false);

    const [openForm, setOpenForm] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState(null);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const { data: empresasData, error: listaEmpresasError } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;

            const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

            setEmpresaIdSession(selectedEmpresaId);

            setSearchParams((state) => {
                state.set('id_empresa', String(selectedEmpresaId));
                state.set('page', String(page + 1));
                state.set('perPage', String(rowsPerPage));

                return state;
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    const queryFilters = {
        id_empresas: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
        nome: searchParams.get('nome'),
        viewOnly: true,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    };

    const queryKeyUnits = ['listOfUnits', queryFilters];
    const { data: unitsData, isLoading, error: unitsError, isFetching, refetch } = useQuery({
        queryKey: queryKeyUnits,
        queryFn: async () => {
            const response = await listaUnidadesDeMedidas(queryFilters);
            return response.data;
        },
        enabled: !!searchParamPage && !!searchParamPerPage,
    });

    function handleEditItem(item) {
        setSelectedUnit(item);
        setOpenForm(true);
    }

    const updateItemStatus = (updatedId, status) => setUnidadesDeMedidas(
        unidadesDeMedidas.map((item) => {
            if (item.id === updatedId) {
                item.status = status;
            }
            return item;
        })
    );

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_unidadeMedidas", "list_unidadeMedidas"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    useEffect(() => {
        if (unitsData) {
            setUnidadesDeMedidas(unitsData.data);
            setNumTotalItems(unitsData.numero_total);
            setHasContract(unitsData.hasContract);
        }
    }, [unitsData]);

    useEffect(() => {
        if (openForm) return;
        setSelectedUnit(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);

    }, [openForm, searchParams]);

    return (<>
        <Helmet title="Unidades de Medida" defer={false} />

        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="end"
            spacing={3}
        >
            <Grid item xs={12} md={8}>
                <Breadcrumbs>
                    <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao >
                    <LinkNavegacao to='/epis'>Gestão de EPI's</LinkNavegacao >
                    <LinkNavegacao to='/epis/produtos'>Produtos</LinkNavegacao >
                    <Typography variant='span'>Unidades de Medidas</Typography>
                </Breadcrumbs>

                <PageTitle
                    icon={<SewingTapeMeasureIcon fontSize='large' />}
                    title='Unidades de Medidas'
                    description='Administre as Unidades de Medidas disponíveis para seus produtos.'
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                    <FiltersButton
                        isOpenFilters={isOpenFilters}
                        setIsOpenFilters={setIsOpenFilters}
                        activeFilters={activeFilters}
                    />

                    <Grid item xs={12} md={4}>
                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setOpenForm(true)}
                                disabled={
                                    !hasPermission(["admin", "admin_unidadeMedidas", "create_unidadeMedidas"])
                                    || !hasContract
                                }
                            >
                                Cadastrar Unidade de Medida
                            </Button>
                        </Typography>
                    </Grid>
                </Stack>
            </Grid>

            <ItemsNoCodeTableFilter
                isOpenFilters={isOpenFilters}
                isLoadingList={isLoading}
                empresas={empresasData?.data?.data || []}
                item={"Unidade de Medida"}
            />

            <Grid item xs={12}>
                {!hasContract && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Contrato não encontrado ou está inativo para esta empresa.
                    </Alert>
                )}

                <TableContainer component={Paper}>
                    <Table aria-label="Unidades de Medida">
                        <TableHead>
                            <TableRow>
                                <TableCell>Unidade de Medida</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Observação</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                    <SkeletonTableRow
                                        numCells={5}
                                        numRows={rowsPerPage < 20 ? rowsPerPage : 5}
                                    />
                                ) : unidadesDeMedidas && unidadesDeMedidas.length > 0 ? (
                                    unidadesDeMedidas.map((item) => (
                                        <UnitOfMeasureRow
                                            key={item.id}
                                            data={item}
                                            handleExibirAlerta={exibirAlerta}
                                            handleOpenEditForm={handleEditItem}
                                            handleUpdateStatus={updateItemStatus}
                                            updateData={refetch}
                                            hasContract={hasContract}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={5} />
                                )
                            }
                        </TableBody>
                    </Table>

                    <CustomTablePagination
                        numTotalItems={numTotalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                </TableContainer>
            </Grid>
        </Grid>

        <UnitsOfMeasureForm
            open={openForm}
            setOpen={setOpenForm}
            selectedUnit={selectedUnit}
            empresas={empresasData?.data?.data || []}
            updateData={refetch}
        />
    </>);
}

export default ListOfUnitsOfMeasure;